import { useMutation, useQueryClient } from '@tanstack/react-query';
import type z from 'zod';

import { useCogniteFunctions } from '#hooks/fusion/useCogniteFunctions';
import { useCogniteClient } from '#hooks/useCogniteClient';
import { CogniteFunctionCallResponseType } from '#schema/model/api/CogniteFunctionCallResponseType';
import { CogniteFunctionCallType } from '#schema/model/api/CogniteFunctionCallType';

class CogniteFunctionCallError extends Error {}
class CogniteFunctionTimeoutError extends Error {}

export type CogniteFunctionDefinition = {
  externalId: string;
  request: z.ZodType;
  response: z.ZodType;
};

export function useCogniteFunction<
  const FunctionDefinitionType extends CogniteFunctionDefinition,
  const RequestType extends z.input<FunctionDefinitionType['request']>,
  const ResponseType extends z.output<FunctionDefinitionType['response']>,
>(
  cogniteFunction: FunctionDefinitionType,
): {
  call: (functionParams?: RequestType, onUpdate?: (status: string) => void) => Promise<ResponseType>;
} {
  const functionExternalId = cogniteFunction.externalId;
  const client = useCogniteClient();
  const { data: cogniteFunctions } = useCogniteFunctions();
  const functionsByExternalId = new Map(cogniteFunctions?.map((fn) => [fn.externalId, fn]));
  const functionId = functionsByExternalId.get(functionExternalId)?.id;
  const pollingIntervalMs = 2000;
  const maxPollingTimeMs = 10 * 60 * 1000;
  const maxPollingAttempts = maxPollingTimeMs / pollingIntervalMs;
  const queryClient = useQueryClient();

  if (!functionId) {
    throw new Error(`Function with externalId '${functionExternalId}' not found`);
  }

  const mutation = useMutation({
    mutationFn: async (params: RequestType) => {
      const sessions = await client?.sessions.create([{ oneshotTokenExchange: true }]);
      const nonce = sessions?.[0]?.nonce;

      return client?.post(`/api/v1/projects/${client.project}/functions/${functionId}/call`, {
        data: {
          data: params,
          nonce,
        },
      });
    },
  });

  const call = async (functionParams: RequestType = {} as RequestType, onUpdate?: (status: string) => void) => {
    try {
      onUpdate?.('starting');

      const parsedParams = cogniteFunction.request.parse(functionParams) as RequestType;
      console.debug(`Function call to '${functionExternalId}' with params:`, parsedParams);

      const { data } = await mutation.mutateAsync(parsedParams);

      const functionCallResponse = CogniteFunctionCallType.parse(data);

      console.debug(`Function call to '${functionExternalId}' succeeded:`, { functionCallResponse });

      const callId = functionCallResponse?.id;

      onUpdate?.('running');

      const statusQuery = await queryClient.fetchQuery({
        queryKey: ['cogniteFunctionCall', { project: client.project, functionId, callId }] as const,
        queryFn: async ({ queryKey: [, { project, functionId, callId }] }) => {
          const fetchStatus = async () => {
            const resp = await client?.get(`/api/v1/projects/${project}/functions/${functionId}/calls/${callId}`);
            const functionCall = CogniteFunctionCallType.parse(resp?.data);
            if (functionCall.status === 'Completed') {
              return Promise.resolve(functionCall);
            }
            if (functionCall.status === 'Failed') {
              return Promise.reject('Function call failed');
            }
            return new Promise<undefined>((resolve) => setTimeout(resolve, pollingIntervalMs));
          };

          for (let attempt = 0; attempt < maxPollingAttempts; attempt++) {
            console.debug(
              `Polling '${functionExternalId}' function call ${callId} status, attempt ${attempt}/${maxPollingAttempts}`,
            );
            try {
              const status = await fetchStatus();
              if (status) {
                return status;
              }
            } catch (error) {
              console.error('Error fetching function status:', error);
              throw error;
            }
          }
        },
      });

      if (!statusQuery) {
        throw new CogniteFunctionTimeoutError(
          `Function call ${callId} timed out without a response after ${maxPollingTimeMs / 1000} seconds (${maxPollingAttempts} attempts)`,
        );
      }

      onUpdate?.('fetchingResponse');

      const resp = await queryClient.fetchQuery({
        queryKey: ['cogniteFunctionCallResponse', { project: client.project, functionId, callId }] as const,
        queryFn: async ({ queryKey: [, { project, functionId, callId }] }) =>
          client?.get(`/api/v1/projects/${project}/functions/${functionId}/calls/${callId}/response`),
      });

      onUpdate?.('completed');

      const { response } = CogniteFunctionCallResponseType.parse(resp?.data);

      if (response.error !== undefined) {
        throw new CogniteFunctionCallError('An error occurred while running the Cognite function', {
          cause: response.error,
        });
      }

      return cogniteFunction.response?.parse(response) as ResponseType;
    } catch (error) {
      onUpdate?.('error');
      console.error(`Function call to '${functionExternalId}' failed:`, error);
      throw error;
    }
  };

  return { call };
}
